import { APIError } from '@cartwhl/common/api/apiRequest';
import postMediaUpload, {
  type MediaUploadItem,
  type MediaUploadResponseItem,
} from '@cartwhl/common/api/postMediaUpload';
import postMotionFromVideo, {
  type ExportType as ApiExportType,
  type ExportSettings,
} from '@cartwhl/common/api/postMotionFromVideo';
import { navigateTo } from '@cartwhl/common/navigation';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import type { FrameRate } from '@cartwhl/common/types';
import { waitForAsyncState } from '@cartwhl/common/utils';
import { uploadBlobToPresignedUrl } from '@cartwhl/common/utils/mediaUploadThumbnail';
import { handleOrgCreditBlockApiError } from '@cartwhl/common/utils/handleOrgCreditBlockApiError';
import type {
  FilesListFile,
  FilesListHandle,
} from '@cartwhl/ui/input/FilesList';

import * as globalStores from '../../stores/global';
import {
  awaitMediaUploadThumbnail,
  clearMediaUploadThumbnailCache,
} from '../shared/mediaUpload/mediaUploadThumbnailCache';
import type { AssetLibraryMediaSelection } from '../AssetLibrary/assetLibraryMediaSelection';
import type { ComicModel, ExportType, UploadModalState } from './types';

// LocalStorage persistence for export settings
const EXPORT_SETTINGS_STORAGE_KEY = 'cartwheel_batch_comic_export_settings';

type PersistedExportSettings = {
  fileType: ExportType;
  frameRate: FrameRate;
  keyframeSimplification: 'none' | 'simplify';
  includeMesh: boolean;
};

const loadExportSettingsFromStorage = (): Partial<PersistedExportSettings> => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem(EXPORT_SETTINGS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveExportSettingsToStorage = (settings: PersistedExportSettings) => {
  try {
    localStorage.setItem(EXPORT_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving export settings:', error);
  }
};

// Dependencies
type Deps = {
  global: typeof globalStores;
};

// State shape
type State = {
  // File tracking (synced from FilesList)
  fileIds: string[];
  /** Videos selected from the asset library (already uploaded media). */
  libraryVideos: AssetLibraryMediaSelection[];

  // Export settings
  jobName: string;
  /** Set when Run process is blocked for empty job name; cleared when the name is edited. */
  jobNameValidationError: boolean;
  fileType: ExportType;
  frameRate: FrameRate;
  keyframeSimplification: 'none' | 'simplify';
  includeMesh: boolean;

  // Processing options (not persisted; reset each session)
  numPeople: number;
  facialCapture: boolean;
  comicModel: ComicModel;

  // Upload modal state (when "Run process" is clicked)
  uploadModalState: UploadModalState;
  uploadedFilesCount: number;
  totalFilesCount: number;
};

// Load persisted settings (runs once at module load)
const savedSettings = loadExportSettingsFromStorage();

const initialState: State = {
  fileIds: [],
  libraryVideos: [],
  jobName: '', // Never persisted - always starts empty
  jobNameValidationError: false,
  fileType: savedSettings.fileType ?? 'FBX_UNREAL',
  frameRate: savedSettings.frameRate ?? 60,
  keyframeSimplification: savedSettings.keyframeSimplification ?? 'simplify',
  includeMesh: savedSettings.includeMesh ?? true,
  numPeople: 1,
  facialCapture: false,
  comicModel: 'comic4',
  uploadModalState: 'idle',
  uploadedFilesCount: 0,
  totalFilesCount: 0,
};

class BatchComicUploadStore extends BaseStore<State, Deps> {
  // 1. Private fields
  private filesListRefGetter: (() => FilesListHandle | null) | null = null;
  private uploadAbortController: AbortController | null = null;

  // 2. Constructor
  constructor(deps: Deps) {
    super(initialState, deps);
  }

  // 3. Lifecycle: reset, destroy
  reset() {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort();
      this.uploadAbortController = null;
    }

    // Clear FilesList
    this.getFilesListRef()?.clearAll();
    clearMediaUploadThumbnailCache();

    // Reload persisted export settings (they may have been updated)
    const persistedSettings = loadExportSettingsFromStorage();

    // Reset state but preserve export settings from localStorage
    Object.assign(this.state, {
      ...initialState,
      libraryVideos: [],
      fileType: persistedSettings.fileType ?? initialState.fileType,
      frameRate: persistedSettings.frameRate ?? initialState.frameRate,
      keyframeSimplification:
        persistedSettings.keyframeSimplification ??
        initialState.keyframeSimplification,
      includeMesh: persistedSettings.includeMesh ?? initialState.includeMesh,
    });
  }

  destroy() {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort();
    }

    super.destroy();
  }

  // 4. Public methods
  setFilesListRef(getter: () => FilesListHandle | null) {
    this.filesListRefGetter = getter;
  }

  getFilesListRef(): FilesListHandle | null {
    return this.filesListRefGetter?.() ?? null;
  }

  syncFiles(files: FilesListFile[]) {
    this.state.fileIds = files.map((f) => f.id);
  }

  addLibraryVideos(videos: AssetLibraryMediaSelection[]) {
    const existing = new Set(this.state.libraryVideos.map((video) => video.mediaID));
    const next = videos.filter((video) => !existing.has(video.mediaID));
    this.state.libraryVideos = [...this.state.libraryVideos, ...next];
  }

  setLibraryVideos(videos: AssetLibraryMediaSelection[]) {
    this.state.libraryVideos = videos;
  }

  removeLibraryVideo(mediaID: string) {
    this.state.libraryVideos = this.state.libraryVideos.filter(
      (video) => video.mediaID !== mediaID,
    );
  }

  getTotalVideoCount(): number {
    return this.state.fileIds.length + this.state.libraryVideos.length;
  }

  getFileIndex(id: string): number {
    return this.state.fileIds.indexOf(id);
  }

  clearAll() {
    this.state.fileIds = [];
    this.state.libraryVideos = [];
    this.getFilesListRef()?.clearAll();
  }

  addFilesExternal(files: File[]) {
    this.getFilesListRef()?.addFiles(files);
  }

  updateFileType(fileType: ExportType) {
    this.state.fileType = fileType;

    // Update defaults based on file type (matching DownloadFile.tsx logic)
    if (fileType === 'BVH') {
      this.state.includeMesh = false;
      this.state.keyframeSimplification = 'none';
    } else if (fileType === 'MA' || fileType === 'MB') {
      this.state.includeMesh = true;
      this.state.keyframeSimplification = 'simplify';
    }
    // For FBX variants and GLB, keep current settings

    this.saveExportSettings();
  }

  updateJobName(jobName: string) {
    this.state.jobNameValidationError = false;
    this.state.jobName = jobName;
  }

  updateFrameRate(frameRate: FrameRate) {
    this.state.frameRate = frameRate;
    this.saveExportSettings();
  }

  updateKeyframeSimplification(value: 'none' | 'simplify') {
    this.state.keyframeSimplification = value;
    this.saveExportSettings();
  }

  updateIncludeMesh(includeMesh: boolean) {
    this.state.includeMesh = includeMesh;
    this.saveExportSettings();
  }

  updateNumPeople(numPeople: number) {
    if (!Number.isFinite(numPeople)) return;
    this.state.numPeople = Math.max(1, Math.min(4, Math.floor(numPeople)));
    this.syncComicModelForConstraints();
  }

  updateFacialCapture(facialCapture: boolean) {
    this.state.facialCapture = facialCapture;
    this.syncComicModelForConstraints();
  }

  updateComicModel(comicModel: ComicModel) {
    this.state.comicModel = comicModel;
    if (comicModel === 'comic3') {
      this.state.numPeople = 1;
      this.state.facialCapture = false;
    }
  }

  requiresComic4(): boolean {
    return this.state.numPeople > 1 || this.state.facialCapture;
  }

  async runProcess() {
    // Check auth status
    if (this.deps.global.authStore.state.authStatus !== 'user') {
      this.deps.global.requestAppletSignInModal();
      return;
    }

    if (this.getTotalVideoCount() === 0) {
      console.error('No files to process');
      return;
    }

    if (!this.state.jobName.trim()) {
      this.state.jobNameValidationError = true;
      this.deps.global.noticesStore.error('Job name required', 6000);
      return;
    }

    // Set up abort controller for cancellation
    this.uploadAbortController = new AbortController();
    this.state.uploadedFilesCount = 0;

    try {
      let apiKey: string;
      try {
        const apiKeyData = await waitForAsyncState(
          this.deps.global.userStore.state,
          (s) => s.apiKey,
        );
        if (!apiKeyData) {
          this.deps.global.noticesStore.error(
            'Failed to authenticate. Please try again.',
          );
          this.state.uploadModalState = 'idle';
          return;
        }
        apiKey = apiKeyData.apiKey;
      } catch (err) {
        console.error('Failed to get API key:', err);
        this.deps.global.noticesStore.error(
          'Failed to authenticate. Please try again.',
        );
        this.state.uploadModalState = 'idle';
        return;
      }

      const files = this.getFilesListRef()?.getFiles() ?? [];
      const libraryMediaIDs = this.state.libraryVideos.map(
        (video) => video.mediaID,
      );
      this.state.totalFilesCount = files.length + libraryMediaIDs.length;

      // Step 1: Get video metadata and request signed upload links for local files
      this.state.uploadModalState = 'getting_links';

      const mediaItems: MediaUploadItem[] = await Promise.all(
        files.map(async (f) => {
          const { duration, resolution } = await this.getVideoMetadata(f.file);
          return {
            duration,
            resolution,
            extension: this.getFileExtension(f.file.name),
            name: this.getFileNameWithoutExtension(f.file.name),
          };
        }),
      );

      if (this.uploadAbortController.signal.aborted) return;

      let uploadedMediaIDs: string[] = [];

      if (mediaItems.length > 0) {
        const { data: uploadData, error: uploadError } = await postMediaUpload(
          this.deps.global.userStore.state.userInfo?.userID,
          mediaItems,
          apiKey,
        );

        if (uploadError || !uploadData) {
          throw new Error(uploadError?.message || 'Failed to get upload links');
        }

        if (this.uploadAbortController.signal.aborted) return;

        const uploadResponses: MediaUploadResponseItem[] =
          uploadData.mediaUploads;

        // Step 2: Upload files to S3 one by one
        this.state.uploadModalState = 'uploading';

        const CONCURRENCY_LIMIT = 4;
        let completedCount = 0;
        const abortSignal = this.uploadAbortController.signal;

        const uploadTasks = files.map((fileItem, i) => async () => {
          if (abortSignal.aborted) return;
          await this.uploadMediaWithThumbnail(
            uploadResponses[i].mediaUploadURL,
            uploadResponses[i].thumbnailUploadURL,
            fileItem,
            fileItem.file.type || 'video/mp4',
            abortSignal,
          );
          completedCount++;
          this.state.uploadedFilesCount = completedCount;
        });

        const executing = new Set<Promise<void>>();
        for (const task of uploadTasks) {
          if (abortSignal.aborted) return;
          const p = task().then(() => {
            executing.delete(p);
          });
          executing.add(p);
          if (executing.size >= CONCURRENCY_LIMIT) {
            await Promise.race(executing);
          }
        }
        await Promise.all(executing);

        if (this.uploadAbortController.signal.aborted) return;

        uploadedMediaIDs = uploadResponses.map((r) => r.mediaID);
      }

      // Step 3: Start processing with library + uploaded mediaIDs
      this.state.uploadModalState = 'starting_process';

      const mediaIDs = [...libraryMediaIDs, ...uploadedMediaIDs];

      // Build export settings
      const exportSettings: ExportSettings = {
        characterID: 'char-upload-Axel', // TODO: Make this configurable if needed
        exportType: this.mapExportType(this.state.fileType),
        forward: 'Z',
        up: 'Y',
        frameRate: this.state.frameRate,
        frameStepSize: 1,
        moveInPlace: false,
        includeMesh: this.state.includeMesh,
        keyframeCleaning:
          this.state.keyframeSimplification === 'simplify'
            ? 'simplify'
            : 'none',
      };

      const { data: batchData, error: batchError } = await postMotionFromVideo(
        {
          batchName: this.state.jobName,
          createdByUser: this.deps.global.userStore.state.userInfo?.userID,
          mediaIDs,
          exportSettings,
          comicModel: this.state.comicModel,
          facialCapture: this.state.facialCapture || undefined,
          numPeople: this.state.numPeople,
          subscribers: [this.deps.global.userStore.state.userInfo.email],
        },
        apiKey,
      );

      if (batchError || !batchData) {
        if (
          batchError instanceof APIError &&
          handleOrgCreditBlockApiError(
            batchError,
            globalStores.userStore.state.organizationInfo,
          )
        ) {
          this.state.uploadModalState = 'idle';
          return;
        }
        globalStores.noticesStore.error(
          batchError?.message || 'Failed to start processing',
        );
        throw new Error(batchError?.message || 'Failed to start processing');
      }

      if (this.uploadAbortController.signal.aborted) return;

      // Reset upload modal state
      this.state.uploadModalState = 'idle';

      // Navigate to the processing screen with the batch ID
      navigateTo(`/comic/${batchData.batchID}`);
    } catch (error) {
      if (
        (error as Error).message === 'Upload cancelled' ||
        (error as Error).name === 'AbortError'
      ) {
        // User cancelled, already handled in cancelUpload
        return;
      }
      console.error('Error running process:', error);
      this.state.uploadModalState = 'idle';
    }
  }

  cancelUpload() {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort();
      this.uploadAbortController = null;
    }

    this.state.uploadModalState = 'idle';
    this.state.uploadedFilesCount = 0;
    this.state.totalFilesCount = 0;
  }

  // 5. Private methods
  private syncComicModelForConstraints() {
    if (this.requiresComic4()) {
      this.state.comicModel = 'comic4';
    }
  }

  private saveExportSettings() {
    saveExportSettingsToStorage({
      fileType: this.state.fileType,
      frameRate: this.state.frameRate,
      keyframeSimplification: this.state.keyframeSimplification,
      includeMesh: this.state.includeMesh,
    });
  }

  private mapExportType(type: ExportType): ApiExportType {
    const mapping: Record<ExportType, ApiExportType> = {
      BVH: 'bvh',
      FBX_MAYA: 'fbx-maya',
      FBX_BLENDER: 'fbx-blender',
      FBX_UNREAL: 'fbx-unreal',
      FBX_ROBLOX: 'fbx-roblox',
      GLB: 'glb',
      MA: 'ma',
      MB: 'mb',
    };
    return mapping[type];
  }

  private async getVideoMetadata(
    file: File,
  ): Promise<{ duration: number; resolution: string }> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve({
          duration: video.duration,
          resolution: `${video.videoWidth}x${video.videoHeight}`,
        });
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };

      video.src = URL.createObjectURL(file);
    });
  }

  private getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
  }

  private getFileNameWithoutExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.substring(0, lastDot) : filename;
  }

  private async uploadMediaWithThumbnail(
    mediaUploadURL: string,
    thumbnailUploadURL: string,
    listFile: FilesListFile,
    contentType: string,
    signal: AbortSignal,
  ): Promise<void> {
    const thumbnail = await awaitMediaUploadThumbnail(listFile.id, signal);

    await Promise.all([
      uploadBlobToPresignedUrl(
        mediaUploadURL,
        listFile.file,
        contentType,
        signal,
      ),
      thumbnail
        ? uploadBlobToPresignedUrl(
            thumbnailUploadURL,
            thumbnail.blob,
            thumbnail.contentType,
            signal,
          )
        : Promise.resolve(),
    ]);
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: BatchComicUploadStore,
  displayName: 'BatchComicUploadStore',
  staticDeps: { global: globalStores },
});

export type { BatchComicUploadStore };
